/**
 * BegutachtungStep
 * ----------------
 * 3-Spalten-Layout (Besichtigung · Fahrzeugzustand · Schadenhergang) mit
 * Dropdowns je Feld + automatischer Generierung rechtssicherer Textblöcke
 * für die Versicherungs-Praxis. Der Sachverständige kann die Vorschläge
 * jederzeit überschreiben.
 */
import React, { useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardCheck, Save, Loader2, Wand2, Sparkles, Eye, Wrench, AlertTriangle, Car, Trash2 } from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";
import VehicleDamageMap from "@/components/VehicleDamageMap";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// ---- Field configurations — Optionen 1:1 übernommen aus
// AutoGutachter24 (Quelle: https://assessment-redesign.preview.emergentagent.com/),
// damit die im Gutachten erscheinenden Textbausteine markttypisch sind.
const BESICHTIGUNG_FIELDS = [
  { key: "besichtigungsbedingungen", label: "Besichtigungsbedingungen",
    options: [
      "ausreichend",
      "ausreichend, von unten",
      "ausreichend, nachbesichtigt",
      "eingeschränkt",
      "eingeschränkt, unbeweglich",
      "eingeschränkt, Wetter",
      "eingeschränkt, Verschmutzung",
      "unzureichend, Totalschaden",
    ] },
  { key: "hebebuehne", label: "Hebebühne",
    options: ["erforderlich", "nicht erforderlich", "nicht verfügbar"] },
  { key: "besichtigungszustand", label: "Besichtigungszustand",
    options: [
      "undemontiert",
      "undemontiert mit Risiko",
      "teilweise demontiert",
      "demontiert",
      "teilweise repariert",
      "repariert",
      "unrepariert",
      "repariert ohne Altteile",
    ] },
  { key: "identifizierung", label: "Identifizierung",
    options: [
      "Fzg.-Schein Teil I",
      "Fzg.-Brief Teil II",
      "Fzg.-Schein Kopie",
      "Werkstattunterlagen",
      "mündliche Angaben",
      "ausländische Papiere",
      "amtliche Papiere",
      "nicht amtliche Papiere",
    ] },
  { key: "scheckheftgepflegt", label: "Scheckheftgepflegt",
    options: [
      "Ja",
      "Nein",
      "nicht prüfbar",
      "markengebundene Werkstatt",
      "Fachwerkstatt",
      "Vertragswerkstatt",
    ] },
  { key: "probelauf_antrieb", label: "Probelauf Antrieb",
    options: ["durchgeführt", "nicht notwendig", "nicht möglich"] },
  { key: "probefahrt", label: "Probefahrt",
    options: ["durchgeführt", "nicht notwendig", "nicht möglich"] },
  { key: "fehlerspeicher", label: "Fehlerspeicher ausgelesen",
    options: ["Ja", "Nein", "nicht möglich"] },
];

// Sinnvolle Defaults für eine typische Standard-Begutachtung. Werden
// AUTOMATISCH gesetzt, sobald der Step aufgerufen wird und die Felder
// noch leer sind — bestehende User-Eingaben werden NIE überschrieben.
// Der Gutachter kann jede Vorbelegung jederzeit über das Dropdown ändern.
const BESICHTIGUNG_DEFAULTS = {
  besichtigungsbedingungen: "ausreichend",
  besichtigungszustand: "undemontiert",
  hebebuehne: "nicht erforderlich",
  identifizierung: "Fzg.-Schein Teil I",
  scheckheftgepflegt: "Nein",
  probelauf_antrieb: "nicht notwendig",
  probefahrt: "nicht notwendig",
  fehlerspeicher: "Nein",
};

const ZUSTAND_DEFAULTS = {
  allgemeinzustand: "gut",
  karosseriezustand: "gut",
  lackzustand: "gut",
  innenraumzustand: "gut",
};

const ZUSTAND_FIELDS = [
  { key: "allgemeinzustand",   label: "Allgemeinzustand",
    options: ["neuwertig", "sehr gut", "gut", "gepflegt", "mäßig", "ungepflegt", "sehr ungepflegt"] },
  { key: "karosseriezustand",  label: "Karosseriezustand",
    options: ["neuwertig", "sehr gut", "gut", "gepflegt", "mäßig", "ungepflegt", "sehr ungepflegt"] },
  { key: "lackzustand",        label: "Lackzustand",
    options: ["neuwertig", "sehr gut", "gut", "gepflegt", "mäßig", "ungepflegt", "sehr ungepflegt"] },
  { key: "innenraumzustand",   label: "Innenraumzustand",
    options: ["neuwertig", "sehr gut", "gut", "gepflegt", "mäßig", "ungepflegt", "sehr ungepflegt"] },
];

const SCHADEN_FIELDS = [
  { key: "schilderung", label: "Schilderung",
    options: [
      "Unfall",
      "Unfall mit Überschlag",
      "Verkehrsunfall",
      "Auffahrunfall",
      "Parkplatzunfall",
      "Seitenaufprall",
      "Streifschaden",
      "Wildschaden",
      "Hagelschaden",
      "Sturmschaden",
      "Marderbiss",
      "Vandalismus",
      "Brand",
      "Diebstahl",
    ] },
  { key: "kollisionsart", label: "Kollisionsart",
    options: [
      "frontal",
      "seitlich links",
      "seitlich rechts",
      "Heck",
      "streifend",
      "Überschlag",
    ] },
  { key: "unfallprotokoll", label: "Unfallprotokoll",
    options: ["nicht vorhanden", "liegt beim Anspruchsteller", "Kopie im Gutachten"] },
  { key: "plausibilitaet", label: "Plausibilität",
    options: ["plausibel", "nicht plausibel", "nicht prüfbar"] },
  { key: "kompatibilitaet", label: "Kompatibilität",
    options: ["kompatibel", "nicht kompatibel", "nicht prüfbar"] },
];

// ---- Legal text generation (rechtssicher / versicherungstauglich) ----
const QUALITY_PHRASES = {
  "neuwertig":      "in einem neuwertigen Pflege- und Erhaltungszustand",
  "sehr gut":       "in einem sehr guten Pflege- und Erhaltungszustand",
  "gut":            "in einem guten Pflege- und Erhaltungszustand",
  "gepflegt":       "in einem gepflegten Erhaltungszustand",
  "mäßig":          "in einem nur mäßigen Pflege- und Erhaltungszustand",
  "ungepflegt":     "in einem ungepflegten Erhaltungszustand",
  "sehr ungepflegt":"in einem sehr ungepflegten Erhaltungszustand",
};

function buildZustandsText(b, vehicle = {}, customer = {}) {
  const a = b.allgemeinzustand;
  const k = b.karosseriezustand;
  const l = b.lackzustand;
  const i = b.innenraumzustand;

  // Vorspann mit Fahrzeug-Eckdaten (Hersteller, Modell, Kennzeichen, FIN)
  // wird IMMER vorangestellt — auch wenn noch keine Zustands-Dropdowns
  // gewählt sind. Nutzt die Daten aus dem Auftrags-Step.
  const veh = [];
  const hersteller = vehicle.manufacturer || vehicle.make || "";
  const modell = vehicle.model || "";
  const farbe = vehicle.color || "";
  const kennzeichen = vehicle.license_plate || customer.license_plate || "";
  const fin = vehicle.vin || "";
  const ez = vehicle.first_registration || vehicle.ez || "";
  const km = vehicle.mileage || vehicle.km || "";

  const fzgBezeichnung = [hersteller, modell].filter(Boolean).join(" ").trim();
  if (fzgBezeichnung) {
    const farbSuffix = farbe ? ` in der Farbe ${farbe}` : "";
    veh.push(`Bei dem hier begutachteten Fahrzeug handelt es sich um einen ${fzgBezeichnung}${farbSuffix}.`);
  }
  const datenZeilen = [];
  if (kennzeichen) datenZeilen.push(`Amtl. Kennzeichen: ${kennzeichen}`);
  if (fin) datenZeilen.push(`Fahrzeug-Identifizierungs-Nummer (FIN): ${fin}`);
  if (ez) datenZeilen.push(`Erstzulassung: ${ez}`);
  if (km) datenZeilen.push(`Laufleistung lt. Tachoanzeige: ${km} km`);
  if (datenZeilen.length > 0) {
    veh.push(datenZeilen.join("\n"));
  }

  const parts = [];
  if (veh.length > 0) parts.push(veh.join("\n"));
  if (a) parts.push(`Das Fahrzeug befindet sich nach Augenschein des Sachverständigen ${QUALITY_PHRASES[a] || a}.`);
  if (k) parts.push(`Die Karosserie zeigt sich in einem ${k}en Zustand, ohne sichtbare Vorschäden außerhalb des hier dokumentierten Schadenereignisses.`);
  if (l) parts.push(`Die Lackoberfläche ist in einem ${l}en Zustand; alterstypische Erscheinungen wie Steinschläge oder Kleinkratzer sind dabei berücksichtigt.`);
  if (i) parts.push(`Der Innenraum stellt sich in einem ${i}en Erhaltungszustand dar und entspricht der Laufleistung sowie dem Fahrzeugalter.`);
  if (parts.length === 0) return "";
  return parts.join("\n\n");
}

const SCHILDERUNG_PHRASES = {
  "Unfall":                "Nach Schilderung des Geschädigten handelt es sich um ein Unfallereignis.",
  "Unfall mit Überschlag": "Nach Schilderung des Geschädigten handelt es sich um einen Verkehrsunfall mit Überschlag.",
  "Verkehrsunfall":        "Es handelt sich um einen Verkehrsunfall im öffentlichen Straßenverkehr.",
  "Auffahrunfall":         "Nach Schilderung des Geschädigten handelt es sich um einen Auffahrunfall. Das hier begutachtete Fahrzeug stand bzw. fuhr verkehrsgerecht und wurde von einem nachfolgenden Fahrzeug heckseitig beschädigt.",
  "Parkplatzunfall":       "Es handelt sich nach Angaben des Geschädigten um einen Parkplatzunfall, bei dem das hier begutachtete Fahrzeug durch ein anderes Fahrzeug beschädigt wurde.",
  "Seitenaufprall":        "Es handelt sich um einen Seitenaufprall.",
  "Streifschaden":         "Es handelt sich um einen Streifschaden, bei dem ein Drittfahrzeug seitlich am hier begutachteten Fahrzeug entlangstreifte.",
  "Wildschaden":           "Es handelt sich um einen Wildunfall. Schadenbild und Beschädigungsspuren stehen mit einer Kollision mit Wild im Einklang.",
  "Hagelschaden":          "Es liegt ein Hagelschaden vor. Über die gesamte Fahrzeugoberfläche sind hagelschlagtypische Eindellungen feststellbar.",
  "Sturmschaden":          "Es handelt sich um einen Sturmschaden; die Beschädigungen sind auf Sturmeinwirkung zurückzuführen.",
  "Marderbiss":            "Es liegt ein Marderschaden im Motorraum vor; die Bissspuren an Leitungen und Dämmmatten sind marderschadentypisch.",
  "Vandalismus":           "Es handelt sich um einen Vandalismusschaden. Die Schäden sind als mutwillige Beschädigung durch unbekannte Täter zu werten.",
  "Brand":                 "Es liegt ein Brandschaden vor; das Schadenbild ist mit einer thermischen Einwirkung kompatibel.",
  "Diebstahl":             "Es handelt sich um Beschädigungen im Zusammenhang mit einem Diebstahl- bzw. Einbruchsereignis.",
};

const KOLLISIONSART_PHRASES = {
  "frontal":          "Die Kollision erfolgte frontal.",
  "seitlich links":   "Die Kollision erfolgte seitlich links.",
  "seitlich rechts":  "Die Kollision erfolgte seitlich rechts.",
  "Heck":             "Die Kollision erfolgte heckseitig.",
  "streifend":        "Die Kollision war streifender Natur.",
  "Überschlag":       "Beim Unfallereignis kam es zu einem Überschlag des Fahrzeugs.",
};

const PROTOKOLL_PHRASES = {
  "nicht vorhanden":           "Ein Unfallprotokoll wurde vom Geschädigten nicht beigebracht.",
  "liegt beim Anspruchsteller":"Ein schriftliches Unfallprotokoll liegt beim Anspruchsteller vor.",
  "Kopie im Gutachten":        "Eine Kopie des Unfallprotokolls befindet sich in den Gutachtenunterlagen.",
};

const PLAUS_PHRASES = {
  "plausibel":      "Der geschilderte Schadenhergang ist nach Augenschein und technischer Erfahrung des Sachverständigen plausibel und steht im Einklang mit den am Fahrzeug vorgefundenen Beschädigungen.",
  "nicht plausibel":"Der geschilderte Schadenhergang ist anhand der dokumentierten Beschädigungen nach Beurteilung des Sachverständigen technisch nicht plausibel. Eine abschließende Klärung wäre nur durch weiterführende Untersuchungen möglich.",
  "nicht prüfbar":  "Eine abschließende Plausibilitätsprüfung war auf Basis der vorliegenden Daten nicht möglich.",
};

const KOMPAT_PHRASES = {
  "kompatibel":     "Die festgestellten Schadenbilder sind mit dem geschilderten Schadenhergang kompatibel.",
  "nicht kompatibel":"Die festgestellten Schadenbilder sind mit dem geschilderten Schadenhergang nicht kompatibel. Es ist von einer abweichenden oder zusätzlichen Schadenursache auszugehen.",
  "nicht prüfbar":  "Die Kompatibilität konnte mangels ausreichender Anhaltspunkte nicht abschließend beurteilt werden.",
};

function buildSchadenText(b) {
  const out = [];
  if (b.schilderung) {
    out.push(SCHILDERUNG_PHRASES[b.schilderung] || `Es handelt sich um ein Schadenereignis der Kategorie „${b.schilderung}".`);
  }
  if (b.kollisionsart && KOLLISIONSART_PHRASES[b.kollisionsart]) {
    out.push(KOLLISIONSART_PHRASES[b.kollisionsart]);
  }
  if (b.unfallprotokoll && PROTOKOLL_PHRASES[b.unfallprotokoll]) {
    out.push(PROTOKOLL_PHRASES[b.unfallprotokoll]);
  }
  if (b.plausibilitaet && PLAUS_PHRASES[b.plausibilitaet]) {
    out.push(PLAUS_PHRASES[b.plausibilitaet]);
  }
  if (b.kompatibilitaet && KOMPAT_PHRASES[b.kompatibilitaet]) {
    out.push(KOMPAT_PHRASES[b.kompatibilitaet]);
  }
  return out.join(" ");
}

// ---- Component ----
export default function BegutachtungStep({
  value,
  onChange,
  onSave,
  saving = false,
  readOnly = false,
  caseId = null,
  vehicle = {},
  customer = {},
  preDamages = {},
  preDamagesArrows = [],
  onPreDamagesChange,
  onPreDamagesArrowsChange,
}) {
  const b = value || {};

  // Defaults einmal beim Mount setzen (oder bei case-id-Wechsel),
  // aber NUR für Felder die noch leer sind. So überschreiben wir keine
  // bestehenden Auswahlen.
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (readOnly) return;
    const missing = {};
    Object.entries({ ...BESICHTIGUNG_DEFAULTS, ...ZUSTAND_DEFAULTS }).forEach(([k, v]) => {
      if (!b[k]) missing[k] = v;
    });
    // Zustandsbeschreibung mit Fahrzeug-Daten vorausfüllen, wenn leer.
    // Wir nutzen die Zustand-Defaults (alle "gut") + die Vehicle-Daten,
    // sodass nach dem Mount sofort ein vollständiger Vorschlag steht.
    if (!b.zustandsbeschreibung) {
      const merged = { ...b, ...missing };
      const text = buildZustandsText(merged, vehicle, customer);
      if (text) missing.zustandsbeschreibung = text;
    }
    if (Object.keys(missing).length > 0) {
      onChange({ ...b, ...missing });
    }
    defaultsAppliedRef.current = true;
  }, [caseId]);

  /**
   * Auto-Übernahme der Textbausteine:
   * Sobald ein Dropdown-Wert geändert wird, regenerieren wir auch die
   * zugehörige Textblock-Spalte (Zustands- bzw. Schadens-Text) AUTOMATISCH —
   * es sei denn, der User hat dort schon manuell etwas Eigenes hineingeschrieben.
   * So muss niemand mehr auf einen „Generieren"-Button klicken; der
   * Textbaustein wird sofort in das Gutachten eingepflegt.
   */
  const set = (k, v) => {
    const next = { ...b, [k]: v };
    const ZUSTAND_KEYS = ["allgemeinzustand", "karosseriezustand", "lackzustand", "innenraumzustand"];
    const SCHADEN_KEYS = ["schilderung", "kollisionsart", "unfallprotokoll", "plausibilitaet", "kompatibilitaet"];

    // Helper — wurde das Textfeld vom User abgewandelt?
    const isAutogenerated = (currentText, build) => {
      if (!currentText) return true; // leer → wir dürfen schreiben
      // Wenn der aktuelle Text einer früheren Auto-Generierung entspricht
      // (also dem Build aus dem alten Wert), gilt er als nicht manuell editiert.
      return currentText.trim() === build(b, vehicle, customer).trim();
    };

    if (ZUSTAND_KEYS.includes(k)) {
      if (isAutogenerated(b.zustandsbeschreibung, buildZustandsText)) {
        next.zustandsbeschreibung = buildZustandsText(next, vehicle, customer);
      }
    }
    if (SCHADEN_KEYS.includes(k)) {
      if (isAutogenerated(b.detaillierte_schilderung, buildSchadenText)) {
        next.detaillierte_schilderung = buildSchadenText(next);
      }
    }
    onChange(next);
  };

  const generateZustand = () => onChange({ ...b, zustandsbeschreibung: buildZustandsText(b, vehicle, customer) });
  const generateSchaden = () => onChange({ ...b, detaillierte_schilderung: buildSchadenText(b) });

  // ── KI-Schadenbeschreibung aus Stichwörtern ────────────────────────────
  // Der Gutachter tippt ein paar Stichwörter (z.B. "Heckkollision, Stillstand
  // vor Ampel, Heckklappe verbogen, Querträger eingedrückt") — die KI baut
  // daraus eine vollständige, juristisch-korrekte Schadenbeschreibung.
  // Bestehender Text wird durch den neuen ersetzt (vorher Bestätigungs-Dialog
  // wenn schon was drinsteht).
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  const generateWithAI = async () => {
    const keywords = aiKeywords.trim();
    if (!keywords) {
      toast.error("Bitte ein paar Stichwörter eingeben (z.B. 'Heckkollision, Stillstand, Querträger').");
      return;
    }
    if ((b.detaillierte_schilderung || "").trim()) {
      const ok = window.confirm(
        "Es ist bereits ein Text in der Schadenbeschreibung vorhanden. Soll er durch den KI-Vorschlag ersetzt werden?"
      );
      if (!ok) return;
    }
    setAiGenerating(true);
    try {
      const payload = { keywords };
      if (caseId) payload.case_id = caseId;
      const { data } = await api.post("/ai/generate-damage-description", payload, { timeout: 60_000 });
      if (data?.text) {
        onChange({ ...b, detaillierte_schilderung: data.text });
        setAiKeywords(""); // Felder leeren für nächste Generation
        toast.success("Schadenbeschreibung von KI erstellt");
      } else {
        toast.error("KI hat keinen Text geliefert.");
      }
    } catch (e) {
      toast.error(apiError(e, "KI-Generierung fehlgeschlagen"));
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="w-full space-y-6" data-testid="step-begutachtung">
      <div className="flex items-start gap-4 pb-4 border-b border-zinc-200">
        <div className="h-11 w-11 rounded-xl brand-gradient-soft flex items-center justify-center shrink-0">
          <ClipboardCheck className="h-5 w-5 text-cyan-700" />
        </div>
        <div>
          <div className="font-display text-xl font-bold tracking-tight">Begutachtung</div>
          <div className="text-sm text-zinc-500">
            Schadenhergang · Besichtigung · Fahrzeugzustand — mit automatischer Erzeugung
            versicherungstauglicher Textblöcke aus Ihrer Auswahl.
          </div>
        </div>
      </div>

      {/* Tabs: 1) Bewertung (klassische Begutachtung)
                2) Schaden-Grafik (Damage-Map für Vor- und Neuschaden) */}
      <Tabs defaultValue="bewertung" className="w-full" data-testid="begutachtung-tabs">
        <TabsList className="grid w-full grid-cols-2 mb-4 max-w-md" data-testid="begutachtung-tabs-list">
          <TabsTrigger value="bewertung" data-testid="begutachtung-tab-bewertung">
            <ClipboardCheck className="h-4 w-4 mr-1.5" />
            Bewertung
          </TabsTrigger>
          <TabsTrigger value="diagramm" data-testid="begutachtung-tab-diagramm">
            <Car className="h-4 w-4 mr-1.5" />
            Schaden-Grafik
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bewertung" className="mt-0 space-y-6">
          {/* 3-spaltig auf Desktop, gestapelt auf Mobile */}
          <div className="grid lg:grid-cols-3 gap-6">
        {/* === Spalte 1: Schadenhergang === */}
        <Column icon={AlertTriangle} title="Schadenhergang" accent="amber" testid="begutachtung-col-schaden">
          {SCHADEN_FIELDS.map((f) => (
            <DropdownField
              key={f.key}
              label={f.label}
              options={f.options}
              value={b[f.key] || ""}
              onChange={(v) => set(f.key, v)}
              disabled={readOnly}
              testid={`begutachtung-${f.key}`}
            />
          ))}
          <div>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Detaillierte Schilderung
              </Label>
              {!readOnly && (
                <div className="flex items-center gap-1.5">
                  <VoiceRecorder
                    compact
                    onTranscript={(t) => set("detaillierte_schilderung", ((b.detaillierte_schilderung || "").trim() ? (b.detaillierte_schilderung + "\n\n") : "") + t)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={generateSchaden} className="h-7 px-2 text-amber-700 hover:bg-amber-50 text-[11px]" data-testid="begutachtung-gen-schaden">
                    <Wand2 className="h-3 w-3 mr-1" /> Aus Auswahl erzeugen
                  </Button>
                </div>
              )}
            </div>

            {/* KI-Generator: Stichwörter → ausformulierter Text via Claude */}
            {!readOnly && (
              <div
                className="mb-2 p-2.5 rounded-md bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200"
                data-testid="begutachtung-ai-block"
              >
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold text-violet-800 mb-1.5">
                  <Sparkles className="h-3 w-3" />
                  KI-Schadenbeschreibung aus Stichwörtern
                </div>
                <div className="flex gap-1.5">
                  <Input
                    value={aiKeywords}
                    onChange={(e) => setAiKeywords(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !aiGenerating) {
                        e.preventDefault();
                        generateWithAI();
                      }
                    }}
                    disabled={aiGenerating}
                    maxLength={1000}
                    placeholder="z.B. Heckkollision, Stillstand vor Ampel, Heckklappe verbogen, Querträger eingedrückt"
                    className="text-[12px] h-8 bg-white border-violet-300 focus-visible:ring-violet-400"
                    data-testid="begutachtung-ai-input"
                  />
                  <Button
                    type="button"
                    onClick={generateWithAI}
                    disabled={aiGenerating || !aiKeywords.trim()}
                    size="sm"
                    className="h-8 px-3 text-[11.5px] font-semibold text-white bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 shrink-0"
                    data-testid="begutachtung-ai-generate"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        formuliert…
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3 mr-1" />
                        Generieren
                      </>
                    )}
                  </Button>
                </div>
                <div className="mt-1 text-[10px] text-violet-700/80">
                  Tipp: Komma-getrennte Stichworte. Die KI baut daraus Hergang + Schadenbild + Plausibilität.
                </div>
              </div>
            )}

            <Textarea
              rows={8}
              value={b.detaillierte_schilderung || ""}
              onChange={(e) => set("detaillierte_schilderung", e.target.value)}
              disabled={readOnly}
              placeholder="Rechtssicherer Stellungnahme-Text — wird aus Schilderung + Plausibilität + Kompatibilität gebildet oder per KI aus Stichwörtern erzeugt."
              data-testid="begutachtung-detaillierte-schilderung"
            />
          </div>
        </Column>

        {/* === Spalte 2: Besichtigung === */}
        <Column icon={Eye} title="Besichtigung" accent="cyan" testid="begutachtung-col-besichtigung">
          {BESICHTIGUNG_FIELDS.map((f) => (
            <DropdownField
              key={f.key}
              label={f.label}
              options={f.options}
              value={b[f.key] || ""}
              onChange={(v) => set(f.key, v)}
              disabled={readOnly}
              testid={`begutachtung-${f.key}`}
            />
          ))}
        </Column>

        {/* === Spalte 3: Fahrzeugzustand === */}
        <Column icon={Wrench} title="Fahrzeugzustand" accent="emerald" testid="begutachtung-col-zustand">
          {ZUSTAND_FIELDS.map((f) => (
            <DropdownField
              key={f.key}
              label={f.label}
              options={f.options}
              value={b[f.key] || ""}
              onChange={(v) => set(f.key, v)}
              disabled={readOnly}
              testid={`begutachtung-${f.key}`}
            />
          ))}
          <div>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Zustandsbeschreibung
              </Label>
              {!readOnly && (
                <div className="flex items-center gap-1.5">
                  <VoiceRecorder
                    compact
                    onTranscript={(t) => set("zustandsbeschreibung", ((b.zustandsbeschreibung || "").trim() ? (b.zustandsbeschreibung + "\n\n") : "") + t)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={generateZustand} className="h-7 px-2 text-emerald-700 hover:bg-emerald-50 text-[11px]" data-testid="begutachtung-gen-zustand">
                    <Wand2 className="h-3 w-3 mr-1" /> Aus Auswahl erzeugen
                  </Button>
                </div>
              )}
            </div>
            <Textarea
              rows={6}
              value={b.zustandsbeschreibung || ""}
              onChange={(e) => set("zustandsbeschreibung", e.target.value)}
              disabled={readOnly}
              placeholder='Vorschlagstext mit "Aus Auswahl erzeugen" einfügen oder frei formulieren.'
              data-testid="begutachtung-zustandsbeschreibung"
            />
          </div>
        </Column>
          </div>
        </TabsContent>

        {/* ───── Schaden-Grafik Tab ────────────────────────────────────────
             Eine einzige Damage-Map — identisch zur User-Seite. Der
             Gutachter/Admin klickt auf ein Karosserieteil und kann pro
             Bauteil eine oder mehrere Status-Marker setzen:
             „neu" (aktueller Unfall), „alt" (Altschaden) oder „vor"
             (Vorschaden bereits repariert). Wenn der User am Smartphone
             selbst nicht in die Grafik geklickt hat, kann der Admin das
             hier nachpflegen. Daten landen in case.pre_damages mit
             statuses-Array. */}
        <TabsContent value="diagramm" className="mt-0 space-y-6">
          <div className="rounded-xl border border-zinc-200 bg-amber-50/40 p-3 text-[12px] text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <div>
              <strong className="font-semibold">Tipp:</strong> Wenn der Geschädigte am Smartphone
              nicht in die Grafik geklickt hat, kannst du die Schadenstellen hier nachpflegen —
              klicke ein Bauteil an und wähle <strong>Neu</strong>, <strong>Alt</strong> oder
              <strong> Vor</strong>-schaden. Mehrfachauswahl möglich.
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="diagramm-block">
            <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-zinc-200/60 bg-zinc-50/60">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Car className="h-4 w-4 text-emerald-700" strokeWidth={1.9} />
                </div>
                <div className="font-display font-semibold text-sm text-zinc-700">
                  Schaden-Erfassung am Fahrzeug
                </div>
              </div>
              {!readOnly && Object.keys(preDamages).length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm("Alle markierten Schäden zurücksetzen?")) {
                      onPreDamagesChange?.({});
                      onPreDamagesArrowsChange?.([]);
                    }
                  }}
                  className="h-7 text-[11px] text-rose-600 hover:bg-rose-50"
                  data-testid="diagramm-clear"
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Zurücksetzen
                </Button>
              )}
            </div>
            <div className="p-3">
              <VehicleDamageMap
                value={preDamages}
                arrows={preDamagesArrows}
                onChange={onPreDamagesChange || (() => {})}
                onArrowsChange={onPreDamagesArrowsChange || (() => {})}
                readOnly={readOnly}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {onSave && !readOnly && (
        <div className="flex gap-3">
          <Button onClick={onSave} disabled={saving} className="brand-gradient-bg text-white" data-testid="begutachtung-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Begutachtung speichern
          </Button>
        </div>
      )}
    </div>
  );
}

// ---- Sub-components ----
const ACCENT = {
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50/40",    text: "text-cyan-700",    iconBg: "bg-cyan-100" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/40", text: "text-emerald-700", iconBg: "bg-emerald-100" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50/40",   text: "text-amber-700",   iconBg: "bg-amber-100" },
};

function Column({ icon: Icon, title, accent, children, testid }) {
  const a = ACCENT[accent] || ACCENT.cyan;
  return (
    <div className={`rounded-2xl border ${a.border} ${a.bg} overflow-hidden`} data-testid={testid}>
      <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-200/60 bg-white/60">
        <div className={`h-8 w-8 rounded-lg ${a.iconBg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${a.text}`} strokeWidth={1.9} />
        </div>
        <div className={`font-display font-semibold text-sm ${a.text}`}>{title}</div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function DropdownField({ label, options, value, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="mt-1.5" data-testid={testid}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
